import React from 'react'
import { Link } from 'react-router-dom'

import { CountrySelect, Fieldset, HintToggleButton, InputText, Legend } from '@paytrace/pt-skel/Component'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { getSubmittedData } from '@paytrace/pt-skel/Helper'
import { useMountUnmount } from '@paytrace/pt-skel/Hooks'
import Event from '../Event'

interface Props {}

const self = Symbol('VisaL3Default')

export function VisaL3Default (props: Props) {
  useMountUnmount(() => {
    PubSub.sub(self, Event.SEARCH_TRANSACTIONS_RESPONSE, (json) => {
      // TODO: handle the response
    })

    return () => {
      PubSub.unsub(self)
    }
  })

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    PubSub.pub('VALIDATION_ERROR::clear')

    // TODO: properly map to the request
    const formData = getSubmittedData(e)
    PubSub.pub(
      Event.SEARCH_TRANSACTIONS_REQUEST,
      { ...formData }
    )

    return false
  }

  return (
    <>
      <div className="float-right">
        <HintToggleButton />
        <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2532343831/Visa+Level+2+3+Default+Data">Help</a>
      </div>
      <h1>Set Default Data - Visa</h1>
      <hr className="clear"/>

      <Link to="/level3.pay">Return to Add Level 3 Data</Link>

      <DataForm onSubmit={submit}/>
    </>
  )
}

function DataForm (props: { onSubmit: (e: React.FormEvent<HTMLFormElement>) => any }) {
  return (
    <form onSubmit={props.onSubmit}>
      <Fieldset>
        <article className="text">
          <p>Many organizations, maybe including your customers and their credit card issuing banks, may use the
            values listed on this page for a variety of reasons from reconciling invoices to managing risk. Taking the
            time to customize these fields now and applying unique values prior to settlement can save your
            organization time and money.</p>
          <p>It is every merchant&apos;s responsibility to review each sale for accuracy and validity. Rules for
            qualifications can and do change. Please contact your merchant service provider for questions about how
            these values may affect your organization.</p>
          <p>You may use this page to set personalized values that may apply to eligible commercial credit card
            transactions. These values will be used to customize text fields on pages when you run transactions. In
            our experience, some of these values may be better left blank on this page. Review the fields identified
            below and their respective support text by hovering your mouse over the field.</p>
          <p>When the tables seem to be tilted away from merchants, PayTrace strives to be a source of support and
            advocacy for you. PayTrace exists to make merchants happy. We do this by educating our customers about the
            payment landscape and delivering innovative solutions that save resources, improve operational efficiency,
            and increase data security.</p>
        </article>

        <Legend title="Level 2 Data"/>

        <InputText
          label="Customer Reference ID"
          name="custrefid"
          maxLength={50}
          hintText="We recommend leaving this field blank so that data from each unique customer and/or transaction may be used.  Please provide an identifier for this customer of up to 15 characters. The default customer ID is the PayTrace transaction ID."
        />
        <InputText
          label="Local Tax (%)"
          name="localtax"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the local tax amount that is included in the transaction amount. The local tax amount may contain up to 12 numeric digits and a decimal. The default value is 5; however, it's strongly recommended to provide a local tax between 0.1% and 21% to ensure that the transaction qualifies."
        />
        <InputText
          label="National Tax"
          name="ntax"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the national tax amount that is included in the transaction amount. The national tax amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />
      </Fieldset>
      <Fieldset>
        <Legend title="Enhanced Data"/>

        <InputText
          label="Merchant Tax ID"
          name="mvat"
          placeholder="See Hints"
          maxLength={20}
          hintText="We recommend providing the tax ID for your business. If this is unavailable, we recommend leaving this field blank so that data from each unique transaction may be used. Please provide your business Tax ID or other value that will help your customer identify your business. The merchant ID may contain up to 20 characters of free text."
        />
        <InputText
          label="Customer Tax ID"
          name="cvat"
          placeholder="See Hints"
          maxLength={13}
          hintText="We recommend leaving this field blank so that data from each unique customer and/or transaction may be used.  Please provide your customer's Tax ID or other value that will help you identify your customer. The customer ID may contain up to 13 characters of free text."
        />
        <InputText
          label="Commodity Code"
          name="apsrccode"
          maxLength={4}
          placeholder="Optional"
          hintText="We recommend providing the most descriptive commodity code that describes the majority of your products.  Please provide the first 4 characters of the commodity code that best describes your business. Commodity codes are maintained by the International Standards Organization (ISO)."
        />
        <InputText
          label="Discount Amount"
          name="disc"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the discount amount that is included in the transaction amount.  The discount amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />
        <InputText
          label="Freight Amount"
          name="freight"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the freight amount that is included in the transaction amount.  The freight amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />
        <InputText
          label="Duty Amount"
          name="duty"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the duty amount that is included in the transaction amount.  The duty amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />
        <InputText
          label="Destination Zip"
          name="dzip"
          maxLength={9}
          placeholder="Optional"
          hintText="Please provide the destination zip where the product/order will be shipped."
        />
        <InputText
          label="Source Zip"
          name="szip"
          maxLength={9}
          placeholder="Optional"
          hintText="Please provide the zip from where the product/order will be shipped."
        />

        <CountrySelect
          label='Destination Country'
          name='country'
          hintText="Please select the country where the product/order will be shipped."
        />

        <InputText
          label="Addtnl Tax (Freight)"
          name="vattax"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the additional tax amount that was generated from freight costs.  The additional tax amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />
        <InputText
          label="Addtnl Tax Rate"
          name="vatrate"
          maxLength={5}
          placeholder="Optional"
          hintText="Please provide the rate used to calculate the additional tax amount from freight costs.  The additional tax rate may contain up to 4 numeric digits and a decimal. The default value is 0."
        />
      </Fieldset>
      <Fieldset>
        <Legend title="Line Item Data"/>

        <InputText
          label="Commodity Code"
          name="ccode"
          maxLength={12}
          placeholder="Optional"
          hintText="We recommend providing the most descriptive commodity code that describes the majority of your products. Please provide the first 12 characters of the commodity code that best describes your business. Commodity codes are maintained by the International Standards Organization (ISO)."
        />
        <InputText
          label="Description"
          name="desc"
          maxLength={35}
          placeholder="See Hints"
          hintText="Unless there is a description that applies to the majority of your products, we recommend leaving this field blank so that data from each unique transaction may be used.  Please provide a description of the product(s) included in this line item. Descriptions may contain up to 35 characters of free text."
        />
        <InputText
          label="Product Code"
          name="productcode"
          maxLength={12}
          placeholder="See Hints"
          hintText="Unless there is a product code that applies to the majority of your products, we recommend leaving this field blank so that data from each unique transaction may be used.  Please provide a product code or ID that may uniquely identify this product in your inventory. Product Codes may contain up to 12 characters of free text."
        />
        <InputText
          label="Unit Measure/Code"
          name="measure"
          maxLength={12}
          placeholder="Optional"
          hintText="Please provide the unit measure/code that is used to measure your products, such as pounds, gallons, liters, case, crate, etc. The unit measure/code may contain up to 12 characters of free text."
        />
        <InputText
          label="Addtnl Tax Amount"
          name="tax"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide any additional tax amount that was included in the order amount in reference to the product defined in this line item. The additional tax amount may contain up to 12 numeric digits and a decimal."
        />
        <InputText
          label="Addtnl Tax Rate"
          name="rate"
          maxLength={5}
          placeholder="Optional"
          hintText="Please provide the rate that was used to calculate any additional tax amount that was included in the order amount in reference to the product defined in this line item. The additional tax rate may contain up to 4 numeric digits and a decimal."
        />
        <InputText
          label="Discount"
          name="discount"
          maxLength={13}
          placeholder="Optional"
          hintText="Please provide the discount amount that was applied to the order in reference to this product. The discount amount may contain up to 12 numeric digits and a decimal. The default value is 0."
        />

        <button className="primary-btn form-btn">Save</button>
      </Fieldset>
    </form>
  )
}

export default VisaL3Default
